import LFCBaseCharts from '_components/charts/base/LFCBaseCharts';
import {useFormatWithSettings} from '_logics/LFCFormatUtil';
import {useParseWithSettings} from '_logics/LFCParseUtil';
import {colorPallet} from '_themes/color-pallet/ColorPallet';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {useTheme} from '@mui/material';
import {lightBlue, pink} from '@mui/material/colors';

import {CloudDistanceDigGraphBaseData} from '../types';

/**
 * 引数
 */
interface Props {
  title: string;
  source: CloudDistanceDigGraphBaseData;
  exportFilename?: string;
  height?: string;
  zoomChartId?: string;
  zoomedChartId?: string;
  zoomCharts?: (zoomChartId?: string) => void;
  zoomChartsClose?: (event: {}, reason: 'backdropClick' | 'escapeKeyDown') => void;
  xRenge?: number;
  averageMinus3SD?: any;
  averagePlus3SD?: any;
  averageUpperDistance?: any;
  medianUpperDistance?: any;
}

/**
 * LFCChartsCloudDistanceMinDistribution
 * @param props
 * @returns
 */
const LFCChartsCloudDistanceMinDistribution = React.memo(
  ({
    title,
    source,
    exportFilename,
    height,
    zoomChartId,
    zoomedChartId,
    zoomCharts,
    zoomChartsClose,
    xRenge,
    averageMinus3SD,
    averagePlus3SD,
    averageUpperDistance,
    medianUpperDistance
  }: Props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const {fmtPercent, fmtSizeByMm} = useFormatWithSettings();
    const {parseSize} = useParseWithSettings();
    const [rangeCounts, setRangeCounts] = useState<number[]>([]);
    const [ranges, setRanges] = useState<number[]>([]);

    const [exportData, setExportData] = useState<any>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [x_list, setX_list] = useState<string[]>([]);
    const [legendSelected, setLegendSelected] = useState<any>({});

    useEffect(() => {
      setLegendSelected({
        [t('上限')]: true,
        [t('下限')]: true,
        [t('中央')]: false,
        [t('平均')]: false,
        [t('平均+3σ')]: false,
        [t('平均-3σ')]: false
      });
    }, [source, t]);

    useEffect(() => {
      if (xRenge !== undefined && !isNaN(xRenge) && xRenge > 0) {
        const x_start = parseSize(Math.min(source.lower_distance_min, source.limits_min));
        const x_end = parseSize(Math.max(source.lower_distance_max, source.limits_max));
        const x_range = parseSize(xRenge);

        if (x_start > x_end) {
          console.error('Invalid range: upper_max is less than upper_min.');
          return;
        }

        let current_value = x_start;
        let newRanges: number[] = [];
        let iterationCount = 0; // 安全対策のためのループ回数制限
        const maxIterations = 1000; // 適切な上限回数を設定

        while (current_value <= x_end) {
          if (iterationCount++ > maxIterations) {
            console.error('Infinite loop detected. Breaking the loop.');
            break;
          }
          newRanges.push(parseSize(current_value));
          current_value += x_range;
        }
        newRanges.push(parseSize(current_value));

        newRanges.sort((a, b) => a - b);
        setRanges(newRanges);

        if (newRanges.length > 1) {
          const newRangeCounts = Array(newRanges.length - 1).fill(0);

          source.data_lower_distance.flat().forEach((value: number) => {
            for (let i = 0; i < newRanges.length - 1; i++) {
              const pValue = parseSize(value);
              if (newRanges[i] <= pValue && pValue < newRanges[i + 1]) {
                newRangeCounts[i]++;
                break;
              }
            }
          });
          setRangeCounts(newRangeCounts);
        } else {
          console.log('Ranges array does not have enough elements.');
        }
      }
    }, [xRenge, source.data_lower_distance, source.data_upper_limits, source.data_lower_limits]);

    useEffect(() => {
      // x_listの計算
      const newX_list = ranges
        .slice(0, -1)
        .map((value, index) => `${parseSize(value)}_${parseSize(ranges[index + 1])}`);
      setX_list(newX_list);

      // エクスポートデータの設定
      let _exportData: any[] = [];
      if (rangeCounts.length === newX_list.length && rangeCounts.length > 0) {
        _exportData = rangeCounts.map((value: any, i: number) => {
          return {
            距離: newX_list[i],
            カウント数: value
          };
        });
        setExportData(_exportData);
      } else {
        console.error('rangeCounts and x_list lengths do not match or are empty');
        setExportData([]);
      }

      // 合計カウントを計算し、状態として保存
      const newTotalCount = rangeCounts.reduce((sum, count) => sum + count, 0);
      setTotalCount(newTotalCount);
    }, [rangeCounts, ranges]);

    const makeMin = () => {
      if (x_list[0] !== undefined) {
        let tmp: any = x_list[0].split('_');
        return parseSize(Math.min(Number(tmp[0]), source.limits_min));
      } else {
        return null;
      }
    };

    const makeMax = () => {
      if (x_list[x_list.length - 1] !== undefined) {
        let tmp: any = x_list[x_list.length - 1].split('_');
        return parseSize(Math.max(Number(tmp[1]), source.limits_max));
      } else {
        return null;
      }
    };

    return (
      <div style={{width: '100%'}}>
        <LFCBaseCharts
          option={{
            title: {
              text: title
            },
            grid: {
              top: 40,
              left: 50,
              right: 5,
              bottom: 60
            },
            xAxis: [
              {
                type: 'category',
                data: x_list,
                axisLabel: {
                  interval: 0,
                  rotate: '15',
                  fontSize: 10
                },
                xAxisIndex: 0
              },
              {
                type: 'value',
                axisLabel: {
                  rotate: '15',
                  fontSize: 10
                },
                min: makeMin(),
                max: makeMax(),
                xAxisIndex: 1,
                show: false,
                axisPointer: {
                  show: true
                }
              }
            ],
            yAxis: {
              yAxisIndex: 0,
              type: 'value',
              minInterval: 1,
              max: Math.max(...rangeCounts)
            },
            legend: {
              id: 1,
              type: 'scroll',
              bottom: 0,
              formatter: (prm: any) => {
                return t(prm);
              },
              data: [
                {
                  name: t('上限'),
                  icon: 'circle'
                },
                {
                  name: t('下限'),
                  icon: 'circle'
                },
                {
                  name: t('中央'),
                  icon: 'circle'
                },
                {
                  name: t('平均'),
                  icon: 'circle'
                },
                {
                  name: t('平均-3σ'),
                  icon: 'circle'
                },
                {
                  name: t('平均+3σ'),
                  icon: 'circle'
                }
              ],
              selected: legendSelected
            },
            series: [
              {
                name: t('個'),
                data: rangeCounts,
                type: 'bar',
                barWidth: '100%',
                xAxisIndex: 0,
                color: theme.palette.primary.main
              },
              {
                name: t('上限'),
                type: 'line',
                xAxisIndex: 1,
                color: pink[500],
                markLine: {
                  label: {
                    color: pink[500],
                    formatter: t('上限'),
                    show: true,
                    position: 'end'
                  },
                  symbol: false,
                  lineStyle: {
                    width: 2,
                    type: 'dotted'
                  },
                  data: [
                    {
                      type: 'guide',
                      name: t('上限'),
                      xAxis: parseSize(source.limits_max)
                    }
                  ]
                }
              },

              {
                name: t('下限'),
                type: 'line',
                xAxisIndex: 1,
                color: lightBlue[500],
                markLine: {
                  label: {
                    color: lightBlue[500],
                    formatter: t('下限'),
                    show: true,
                    position: 'end'
                  },
                  symbol: false,
                  lineStyle: {
                    width: 2,
                    type: 'dotted'
                  },
                  data: [
                    {
                      type: 'guide',
                      name: t('下限'),
                      xAxis: parseSize(source.limits_min)
                    }
                  ]
                }
              },
              {
                name: t('中央'),
                type: 'line',
                xAxisIndex: 1,
                color: colorPallet[1],
                markLine: {
                  label: {
                    color: colorPallet[1],
                    formatter: t('中央'),
                    show: true,
                    position: 'end'
                  },
                  symbol: false,
                  lineStyle: {
                    width: 2,
                    type: 'solid'
                  },
                  data: [
                    {
                      type: 'guide',
                      name: t('中央'),
                      xAxis: parseSize(medianUpperDistance)
                    }
                  ]
                }
              },
              {
                name: t('平均'),
                type: 'line',
                xAxisIndex: 1,
                color: colorPallet[2],
                markLine: {
                  label: {
                    color: colorPallet[2],
                    formatter: t('平均'),
                    show: true,
                    position: 'end'
                  },
                  symbol: false,
                  lineStyle: {
                    width: 2,
                    type: 'solid'
                  },
                  data: [
                    {
                      type: 'guide',
                      name: t('平均'),
                      xAxis: parseSize(averageUpperDistance)
                    }
                  ]
                }
              },
              {
                name: t('平均+3σ'),
                type: 'line',
                xAxisIndex: 1,
                color: colorPallet[3],
                markLine: {
                  label: {
                    color: colorPallet[3],
                    formatter: t('平均+3σ'),
                    show: true,
                    position: 'end'
                  },
                  symbol: false,
                  lineStyle: {
                    width: 2,
                    type: 'solid'
                  },
                  data: [
                    {
                      type: 'guide',
                      name: t('平均+3σ'),
                      xAxis: parseSize(averagePlus3SD)
                    }
                  ]
                }
              },
              {
                name: t('平均-3σ'),
                type: 'line',
                xAxisIndex: 1,
                color: colorPallet[4],
                markLine: {
                  label: {
                    color: colorPallet[4],
                    formatter: t('平均-3σ'),
                    show: true,
                    position: 'end'
                  },
                  symbol: false,
                  lineStyle: {
                    width: 2,
                    type: 'solid'
                  },
                  data: [
                    {
                      type: 'guide',
                      name: t('平均-3σ'),
                      xAxis: parseSize(averageMinus3SD)
                    }
                  ]
                }
              }
            ],
            tooltip: {
              trigger: 'item',
              formatter: (prm: any) => {
                if (prm.seriesName === t('個')) {
                  return (
                    prm.name +
                    'mm<br>' +
                    t('検査数') +
                    ':' +
                    prm.value +
                    t('件') +
                    '<br>' +
                    t('構成比') +
                    ':' +
                    fmtPercent((prm.value / totalCount) * 100)
                  );
                } else {
                  return prm.data.name === t('上限')
                    ? t('上限') + ':' + fmtSizeByMm(source.limits_max)
                    : prm.data.name === t('下限')
                      ? t('下限') + ':' + fmtSizeByMm(source.limits_min)
                      : prm.data.name === t('中央')
                        ? t('中央') + ':' + fmtSizeByMm(medianUpperDistance)
                        : prm.data.name === t('平均')
                          ? t('平均') + ':' + fmtSizeByMm(averageUpperDistance)
                          : prm.data.name === t('平均-3σ')
                            ? t('平均-3σ') + ':' + fmtSizeByMm(averageMinus3SD)
                            : prm.data.name === t('平均+3σ')
                              ? t('平均+3σ') + ':' + fmtSizeByMm(averagePlus3SD)
                              : '';
                }
              }
            },
            dataZoom: [
              {
                type: 'inside'
              }
            ]
          }}
          exportData={exportData}
          exportFields={{
            距離: t('距離'),
            カウント数: t('カウント数')
          }}
          exportFilename={exportFilename}
          height={height ? height : ''}
          zoomCharts={zoomCharts ? zoomCharts : undefined}
          zoomChartsClose={zoomChartsClose ? zoomChartsClose : undefined}
          zoomChartId={zoomChartId ? zoomChartId : undefined}
          zoomStatus={zoomChartId === zoomedChartId}
        />
      </div>
    );
  }
);

LFCChartsCloudDistanceMinDistribution.displayName = 'LFCChartsCloudDistanceMinDistribution';

export default LFCChartsCloudDistanceMinDistribution;
